import { call, put, select, takeLatest, fork, all } from 'redux-saga/effects';
import axios from 'axios';
import { SagaIterator } from 'redux-saga';

import { setSubmitHasFailed } from 'services/virtualizedTable/actions';
import { updateQueryString } from 'services/router/actions';
// @ts-ignore
import { idTokenSelector } from 'services/auth/selectors';
// @ts-ignore
import { selectCurrentOrganizationId } from 'services/currentOrganizationId/selectors';
import { ERROR_MODAL_QUERY_STRING_PARAM } from 'components/ErrorModal/constants';

import {
    RequestedCreateActiveIngredientAction,
    RequestedUpdateActiveIngredientAction,
    RequestedDeleteActiveIngredientAction
} from './types';
import {
    succeededCreateActiveIngredient,
    failedCreateActiveIngredient,
    succeededUpdateActiveIngredient,
    failedUpdateActiveIngredient,
    succeededDeleteActiveIngredient,
    failedDeleteActiveIngredient,
    setActiveIngredient
} from './actions';
import {
    ACTIVE_INGREDIENT_CREATE_REQUESTED,
    ACTIVE_INGREDIENT_UPDATE_REQUESTED,
    ACTIVE_INGREDIENT_DELETE_REQUESTED
} from './constants';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* postActiveIngredient(
    action: RequestedCreateActiveIngredientAction
): SagaIterator {
    const [idToken, organizationId] = yield all([
        select(idTokenSelector),
        select(selectCurrentOrganizationId)
    ]);
    const url = `${REACT_APP_API_URL}/v1/ActiveIngredients`;

    try {
        const response = yield call(axios, url, {
            method: 'post',
            headers: {
                Authorization: idToken
            },
            data: {
                ...action.payload,
                organizationId
            }
        });
        yield all([
            put(succeededCreateActiveIngredient(response)),
            put(setActiveIngredient(response.data))
        ]);
    } catch (error) {
        yield all([
            put(failedCreateActiveIngredient(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]: 'ACTIVE_INGREDIENT_CREATE'
                })
            )
        ]);
    }
}

export function* patchActiveIngredient(
    action: RequestedUpdateActiveIngredientAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);

    const {
        payload: { activeIngredientId, activeIngredient }
    } = action;

    try {
        const url = `${REACT_APP_API_URL}/v1/ActiveIngredients/${activeIngredientId}`;

        const response = yield call(axios, url, {
            method: 'patch',
            headers: {
                Authorization: idToken
            },
            data: activeIngredient
        });
        yield all([
            put(succeededUpdateActiveIngredient(response)),
            put(setActiveIngredient(response.data))
        ]);
    } catch (error) {
        yield all([
            put(failedUpdateActiveIngredient(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]: 'ACTIVE_INGREDIENT_UPDATE'
                })
            )
        ]);
    }
}

export function* deleteActiveIngredient(
    action: RequestedDeleteActiveIngredientAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const { activeIngredientId } = action.payload;

    try {
        const url = `${REACT_APP_API_URL}/v1/ActiveIngredients/${activeIngredientId}`;
        const response = yield call(axios, url, {
            method: 'delete',
            headers: {
                Authorization: idToken
            }
        });
        const res = yield call(axios, url, {
            method: 'get',
            headers: {
                Authorization: idToken
            }
        });
        yield all([
            put(succeededDeleteActiveIngredient(response)),
            put(setActiveIngredient(res.data))
        ]);
    } catch (error) {
        yield all([
            put(failedDeleteActiveIngredient(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]: 'ACTIVE_INGREDIENT_DELETE'
                })
            )
        ]);
    }
}

export function* watchCreateActiveIngredient(): SagaIterator {
    yield takeLatest(ACTIVE_INGREDIENT_CREATE_REQUESTED, postActiveIngredient);
}

export function* watchUpdateActiveIngredient(): SagaIterator {
    yield takeLatest(ACTIVE_INGREDIENT_UPDATE_REQUESTED, patchActiveIngredient);
}

export function* watchDeleteActiveIngredient(): SagaIterator {
    yield takeLatest(
        ACTIVE_INGREDIENT_DELETE_REQUESTED,
        deleteActiveIngredient
    );
}

export default function* activeIngredientSetsSagas(): SagaIterator {
    yield all([
        fork(watchCreateActiveIngredient),
        fork(watchUpdateActiveIngredient),
        fork(watchDeleteActiveIngredient)
    ]);
}
